import { useState } from "react";
import { ArrowUpRight, Plus, X } from "lucide-react";

const DEFAULT_UNITS = [
  { name: "box", factor: 100 },
  { name: "strip", factor: 10 },
  { name: "tablet", factor: 1 },
];

export default function ItemForm({ vendors = [], onSubmit, onClose }) {
  const [form, setForm] = useState({
    name: "",
    sku: "",
    category: "Medicine",
    vendor: vendors[0]?.name || "",
    rate: 0,
    base_unit: "tablet",
    stock: 0,
    reorder_level: 10,
    expiry: "",
    batch: "",
    hsn: "",
    gst: 12,
  });
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addUnit = () => setUnits((u) => [...u, { name: "", factor: 1 }]);
  const updateUnit = (i, k, v) => setUnits((u) => u.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeUnit = (i) => setUnits((u) => u.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const cleanUnits = units
      .filter((u) => u.name)
      .map((u) => ({ name: u.name, factor: Number(u.factor) || 1 }));
    // ensure base unit exists in units list (factor 1)
    if (!cleanUnits.find((u) => u.name === form.base_unit)) {
      cleanUnits.push({ name: form.base_unit, factor: 1 });
    }
    try {
      await onSubmit({
        ...form,
        rate: Number(form.rate),
        stock: Number(form.stock),
        reorder_level: Number(form.reorder_level),
        gst: Number(form.gst),
        units: cleanUnits,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>Item name<input data-testid="name-input" value={form.name} onChange={(e) => update("name", e.target.value)} required /></label>
        <label>SKU / Item code<input data-testid="sku-input" value={form.sku} onChange={(e) => update("sku", e.target.value)} /></label>
        <label>Category
          <select data-testid="category-select" value={form.category} onChange={(e) => update("category", e.target.value)}>
            <option>Medicine</option><option>Equipment</option><option>Consumable</option>
          </select>
        </label>
        <label>Supply vendor
          <select data-testid="vendor-select" value={form.vendor} onChange={(e) => update("vendor", e.target.value)}>
            <option value="">Select vendor</option>
            {vendors.map((v) => <option key={v.id || v.name}>{v.name}</option>)}
          </select>
        </label>
        <label>Rate per base unit (₹)<input data-testid="rate-input" type="number" step="0.01" value={form.rate} onChange={(e) => update("rate", e.target.value)} /></label>
        <label>Base unit<input data-testid="base_unit-input" value={form.base_unit} onChange={(e) => update("base_unit", e.target.value)} required /></label>
        <label>Opening stock (base units)<input data-testid="stock-input" type="number" value={form.stock} onChange={(e) => update("stock", e.target.value)} /></label>
        <label>Reorder level<input data-testid="reorder_level-input" type="number" value={form.reorder_level} onChange={(e) => update("reorder_level", e.target.value)} /></label>
        <label>Batch number<input data-testid="batch-input" value={form.batch} onChange={(e) => update("batch", e.target.value)} /></label>
        <label>Expiry date<input data-testid="expiry-input" type="date" value={form.expiry} onChange={(e) => update("expiry", e.target.value)} /></label>
        <label>HSN code<input data-testid="hsn-input" value={form.hsn} onChange={(e) => update("hsn", e.target.value)} /></label>
        <label>GST %<input data-testid="gst-input" type="number" value={form.gst} onChange={(e) => update("gst", e.target.value)} /></label>
      </div>

      <div className="units-block">
        <div className="units-head">
          <div>
            <p className="eyebrow">PACKAGING CONVERSION</p>
            <h4>How does this item ship?</h4>
            <small>Define units and how many base units each contains. e.g. 1 box = 100 tablets.</small>
          </div>
          <button type="button" className="secondary sm" data-testid="add-unit-button" onClick={addUnit}><Plus size={14}/> Add level</button>
        </div>
        <div className="unit-rows">
          {units.map((u, i) => (
            <div className="unit-row" key={i}>
              <input placeholder="Unit name (e.g. box)" data-testid={`unit-name-${i}`} value={u.name} onChange={(e) => updateUnit(i, "name", e.target.value)} />
              <span className="unit-equals">=</span>
              <input placeholder="Factor" data-testid={`unit-factor-${i}`} type="number" value={u.factor} onChange={(e) => updateUnit(i, "factor", e.target.value)} />
              <span className="unit-base">× {form.base_unit || "base"}</span>
              <button type="button" className="icon-btn ghost" onClick={() => removeUnit(i)}><X size={14}/></button>
            </div>
          ))}
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="secondary" onClick={onClose}>Cancel</button>
        <button className="primary" data-testid="form-submit-button" disabled={busy}>{busy ? "Saving…" : "Add item"} <ArrowUpRight size={16}/></button>
      </div>
    </form>
  );
}
